import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { ORDER_PAGE_FLAGS } from '@Orders/logic'
import InvalidConfigModal from '@Orders/OrderDetailsPage/OrderDetailHeaderAndFooter/InvalidConfigModal'

import Button from '~/core/Button'
import logger from '~/lib/logger'
import useIsAdminPage from '~/lib/useIsAdminPage'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import { ADMIN_PATH, QUOTES_PATH, TEAM_PATH } from '~/redux-modules/preferences'
import { getUserIdentity } from '~/redux-modules/identity'
import { openChatDriftReorderGeneral } from '~/redux-modules/drift'

import {
    LoadingNotification,
    OpenCloneQuoteProgressNotification,
    ReorderFailedNotification,
} from './Notifications'
import createReorderQuoteFromOrderMutation from './createReorderQuoteFromOrderMutation'
import getNonconfigurableOrderPartConfigInfoQuery from './getNonconfigurableOrderPartConfigInfoQuery'
import getReorderEligibilityForOrderQuery from './getReorderEligibilityForOrderQuery'
import { formatNonConfigurableInfo } from './logic'
import { getNonconfigurableOrderPartConfigInfo_getNonconfigurableOrderPartConfigInfo as ConfigInfoType } from './__generated__/getNonconfigurableOrderPartConfigInfo'

interface ReorderButtonPropsType {
    name: string
    placement: string
    userId?: number
    style?: { [key: string]: string | number }
    teamId?: number
    isLink?: boolean
}

const ReorderButton = (props: ReorderButtonPropsType) => {
    const {
        name: orderName,
        placement,
        userId,
        teamId,
        isLink = false,
        style,
    } = props
    const client = useApolloClient()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const isLocatedInAdminPage = useIsAdminPage()
    const isReorderEnabled = useFlagEnabled(ORDER_PAGE_FLAGS.REORDER)
    const isReorderInvalidConfigEnabled = useFlagEnabled(
        ORDER_PAGE_FLAGS.INVALID_CONFIG_REORDER,
    )
    const [isInvalidConfigModalEnabled, setInvalidConfigModalEnabled] =
        useState<boolean>(false)
    const [nonConfigurableOrderInfo, setNonConfigurableOrderInfo] = useState<
        ConfigInfoType[]
    >([])
    const [isLoading, setLoading] = useState(false)
    const identity = useSelector(getUserIdentity)

    const onCloseNotification = (cloneQuoteId: string) => {
        setLoading(false)
        let redirectPath = `${QUOTES_PATH}/${cloneQuoteId}`

        if (isLocatedInAdminPage && userId) {
            redirectPath = `${ADMIN_PATH}/user/${userId}/quotes/${cloneQuoteId}`
        }

        if (teamId) {
            redirectPath = `${TEAM_PATH}/${teamId}/quotes/${cloneQuoteId}`
            if (isLocatedInAdminPage) {
                redirectPath = `${ADMIN_PATH}/user/${userId}/team/${teamId}/quotes/${cloneQuoteId}`
            }
        }

        return navigate(redirectPath)
    }

    const onClickReorder = async (evt: Event) => {
        evt.preventDefault()

        if (!isReorderEnabled) {
            return dispatch(
                openChatDriftReorderGeneral(
                    identity.id,
                    orderName,
                    client,
                    `Order: ${orderName} cannot be reordered because reorder feature is turned off`,
                ),
            )
        }

        setLoading(true)
        LoadingNotification(true)

        try {
            const { data: isReorderEligibleData } = await client.query({
                query: getReorderEligibilityForOrderQuery,
                variables: {
                    orderName,
                },
            })

            const canReorder =
                isReorderEligibleData?.getReorderEligibilityForOrder

            if (!canReorder?.eligible) {
                setLoading(false)
                ReorderFailedNotification()

                return dispatch(
                    openChatDriftReorderGeneral(
                        identity.id,
                        orderName,
                        client,
                        canReorder.ineligibilityReason,
                    ),
                )
            }

            const { data: nonConfigurableOrderInfoData } = await client.query({
                query: getNonconfigurableOrderPartConfigInfoQuery,
                variables: {
                    orderName,
                },
            })

            if (nonConfigurableOrderInfo?.length > 0) {
                setLoading(false)
                if (isReorderInvalidConfigEnabled) {
                    setNonConfigurableOrderInfo(
                        nonConfigurableOrderInfoData?.getNonconfigurableOrderPartConfigInfo,
                    )
                    LoadingNotification(false)
                    setInvalidConfigModalEnabled(true)
                }

                ReorderFailedNotification()

                return dispatch(
                    openChatDriftReorderGeneral(
                        identity.id,
                        orderName,
                        client,
                        `Order: ${orderName} has configurations that cannot be reordered in ${formatNonConfigurableInfo(
                            nonConfigurableOrderInfo,
                        )}`,
                    ),
                )
            }

            const { data: reorderData } = await client.mutate({
                mutation: createReorderQuoteFromOrderMutation,
                variables: {
                    orderName,
                    userId: isLocatedInAdminPage && userId ? userId : null,
                },
            })

            return OpenCloneQuoteProgressNotification(() =>
                onCloseNotification(
                    reorderData?.createReorderQuoteFromOrder?.id,
                ),
            )
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'Orders/OrderDetailsPage::Failed to clone quote mutation',
                    orderName,
                },
            })

            setLoading(false)
            ReorderFailedNotification()

            return dispatch(
                openChatDriftReorderGeneral(
                    identity.id,
                    orderName,
                    client,
                    `Order: ${orderName} cannot be reordered because cloning quote failed, contact Eng Team`,
                ),
            )
        }
    }

    return (
        <>
            <Button
                data-test-target="reorder-button-on-order-details-page"
                loading={isLoading}
                name="reorder"
                onClick={onClickReorder}
                placement={placement}
                style={style}
                type={isLink ? 'link' : undefined}
            >
                Reorder
            </Button>
            {isReorderInvalidConfigEnabled && (
                <InvalidConfigModal
                    nonConfigurableInfo={nonConfigurableOrderInfo}
                    onClose={() => setInvalidConfigModalEnabled(false)}
                    orderName={orderName}
                    visible={isInvalidConfigModalEnabled}
                />
            )}
        </>
    )
}

export default ReorderButton
